import { holds, type Capability, type TenantContext } from '@timely/domain';

/**
 * Every address inside a company, and what each place is called. Screens link
 * through `paths()`, never by writing `/w/...` by hand, so the map can change in
 * one place (docs/product/PRODUCT-UX-FOUNDATION.md §4).
 *
 * The navigation is built from capabilities, not role names. Hiding a place is
 * a courtesy: the policies and the server routes refuse the same person if they
 * type the address.
 */

/**
 * An account's tabs, each a page with its own address (Customer 360, decision 6): Overview · Activity · Bills ·
 * Contacts. The ledger has no tab until Phase 2 works.
 */
export type AccountTab = 'overview' | 'activity' | 'bills' | 'contacts';

export type SettingsSection =
    | 'company' | 'setup' | 'team' | 'roles' | 'ownership' | 'data'
    | 'reminders' | 'templates' | 'ai' | 'audit' | 'company-data';

export function paths(slug: string) {
    const w = `/w/${slug}`;
    return {
        today: `${w}/today`,
        customers: `${w}/customers`,
        /** An account always opens on Overview (Customer 360, decision 3). */
        customer: (id: string) => `${w}/customers/${id}`,
        /** One of the account's other tabs, by its own address, for a link that means that tab. */
        customerTab: (id: string, tab: AccountTab) => (tab === 'overview' ? `${w}/customers/${id}` : `${w}/customers/${id}/${tab}`),
        /** The account's statement, as a document to print or save as a PDF. */
        customerStatement: (id: string) => `${w}/customers/${id}/statement`,
        cheques: `${w}/cheques`,
        reports: `${w}/reports`,
        stock: `${w}/stock`,
        settings: `${w}/settings`,
        setting: (section: SettingsSection) => `${w}/settings/${section}`,
        /** Your own name, password and devices: yours, not the company's. */
        account: `${w}/account`,
    };
}

/* ------------------------------ primary places ----------------------------- */

export type PrimaryPlace = 'today' | 'customers' | 'cheques' | 'reports' | 'stock';

export interface PrimaryDestination {
    place: PrimaryPlace;
    label: string;
}

/**
 * The daily work, in the order it is done: what needs doing now, the accounts,
 * the cheques in hand, how it is going. Stock appears only where a stock sheet
 * is connected (or where this person cannot see whether one is).
 * Administration is one entry, Settings, and never competes with these.
 */
export function primaryDestinations(context: TenantContext, stock: { connected: boolean | null }): PrimaryDestination[] {
    const wholeBook = holds(context, 'customers.view.all');
    const wholeReports = holds(context, 'reports.view.all');
    const places: (PrimaryDestination | false)[] = [
        { place: 'today', label: 'Today' },
        { place: 'customers', label: wholeBook ? 'Customers' : 'My customers' },
        holds(context, 'cheques.view') && { place: 'cheques', label: 'Cheques' },
        holds(context, 'reports.view.own') && { place: 'reports', label: wholeReports ? 'Reports' : 'My reports' },
        holds(context, 'stock.view') && stock.connected !== false && { place: 'stock', label: 'Stock' },
    ];
    return places.filter((p): p is PrimaryDestination => Boolean(p));
}

/* --------------------------------- settings -------------------------------- */

export interface SettingsDefinition {
    section: SettingsSection | 'stock';
    label: string;
    /** One line on the Settings page: what is here. */
    summary: string;
    group: 'Company' | 'People' | 'Data' | 'Messages' | 'Records';
    /** Absent means anybody who can open Settings may open it. */
    needs?: Capability;
    needsAny?: readonly Capability[];
    /** Set when the entry opens a place outside Settings rather than a section of it. */
    opens?: 'stock';
}

export const SETTINGS: readonly SettingsDefinition[] = [
    { section: 'company', label: 'Company profile', group: 'Company', needs: 'settings.manage',
        summary: 'Name, logo, currency, time zone and customer categories.' },
    { section: 'setup', label: 'Setup checklist', group: 'Company',
        needsAny: ['datasource.view', 'team.manage', 'integrations.manage', 'settings.manage', 'templates.manage'],
        summary: 'What is connected, what needs attention, and what is still optional.' },
    { section: 'team', label: 'Team', group: 'People', needs: 'team.view',
        summary: 'Who can sign in, their roles, and invitations.' },
    { section: 'roles', label: 'Roles and permissions', group: 'People', needs: 'roles.manage',
        summary: 'What each role may see and do.' },
    { section: 'ownership', label: 'Account owners', group: 'People', needsAny: ['team.manage', 'customers.assign'],
        summary: 'Match the owner codes an imported sheet carries to people on the team. TallyPrime carries none.' },
    { section: 'data', label: 'Receivables data', group: 'Data', needs: 'datasource.view',
        summary: 'Where the balances come from (TallyPrime or a Google Sheet), how current they are, and how they are read.' },
    // Stock is optional and leaves the sidebar until connected, so the way to
    // connect it lives here, with the other sheet.
    { section: 'stock', label: 'Stock sheet', group: 'Data', needs: 'datasource.manage', opens: 'stock',
        summary: 'Optional. A second sheet of stock levels, shown under Stock once connected.' },
    { section: 'reminders', label: 'Reminder emails', group: 'Messages', needs: 'integrations.manage',
        summary: 'The daily email of what is due, and who receives it.' },
    { section: 'templates', label: 'Message templates', group: 'Messages', needs: 'templates.manage',
        summary: 'Reusable wording for payment reminders.' },
    { section: 'ai', label: 'AI summaries', group: 'Messages', needs: 'integrations.manage',
        summary: 'Optional. Your own AI provider key for account summaries.' },
    { section: 'audit', label: 'Audit log', group: 'Records', needs: 'workspace.audit.view',
        summary: 'Who changed what, and when.' },
    { section: 'company-data', label: 'Export and delete', group: 'Records',
        needsAny: ['workspace.export', 'workspace.reset', 'workspace.delete'],
        summary: 'Download everything, clear business data, or delete the company.' },
];

export const SETTINGS_GROUPS: readonly SettingsDefinition['group'][] = ['Company', 'People', 'Data', 'Messages', 'Records'];

export const canOpenSetting = (context: TenantContext, d: SettingsDefinition): boolean =>
    (!d.needs || holds(context, d.needs)) && (!d.needsAny || d.needsAny.some(c => holds(context, c)));

export const visibleSettings = (context: TenantContext): SettingsDefinition[] =>
    SETTINGS.filter(d => canOpenSetting(context, d));

export const settingDefinition = (section: string): SettingsDefinition | undefined =>
    SETTINGS.find(d => d.section === section && !d.opens);

/** Where a Settings entry goes: its section, or the place outside Settings it opens. */
export const settingHref = (slug: string, d: SettingsDefinition): string =>
    d.opens ? paths(slug)[d.opens] : paths(slug).setting(d.section as SettingsSection);

/* ------------------------------- older addresses --------------------------- */

/**
 * Addresses from before the Settings area and the Customers rename, and where
 * each now lives. Saved links and bookmarks keep working.
 */
export const MOVED: Readonly<Record<string, string>> = {
    book: 'customers',
    team: 'settings/team',
    roles: 'settings/roles',
    data: 'settings/data',
    alerts: 'settings/reminders',
    templates: 'settings/templates',
    ai: 'settings/ai',
    audit: 'settings/audit',
    company: 'settings/company-data',
    setup: 'settings/setup',
    reconciliation: 'settings/ownership',
};

/* --------------------------------- titles ---------------------------------- */

const PRIMARY_TITLES: Record<string, string> = {
    today: 'Today', customers: 'Customers', cheques: 'Cheques', reports: 'Reports', stock: 'Stock', settings: 'Settings',
    account: 'Your account',
};

/** What the screen at this address is called, for the page title and the screen reader's heading. */
export function screenName(pathname: string, slug: string): string {
    const base = `/w/${slug}`;
    if (pathname !== base && !pathname.startsWith(`${base}/`)) return 'Timely Payment';
    const [first = '', second] = pathname.slice(base.length).replace(/^\//, '').split('/');
    if (first === 'customers' && second) return 'Customer';
    if (first === 'settings' && second) return settingDefinition(second)?.label ?? 'Settings';
    return PRIMARY_TITLES[first || 'today'] ?? 'Timely Payment';
}
